/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { DEFAULT_LOGO_URL } from "../utils/brandLogo";
const defaultLogoImg = DEFAULT_LOGO_URL;

export interface BrandingConfig {
  siteName?: string;
  tagline?: string;
  logoUrl?: string;
  darkLogoUrl?: string;
  lightLogoUrl?: string;
  dashboardLogoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  headerAnnouncementText?: string;
  showHeaderAnnouncement?: boolean;
}

export interface GeneralConfig {
  platformName?: string;
  companyName?: string;
  companyAddress?: string;
  supportEmail?: string;
  supportPhone?: string;
  whatsappNumber?: string;
  websiteUrl?: string;
  currency?: string;
}

export interface MaintenanceConfig {
  maintenanceMode?: boolean;
  loginMaintenanceMode?: boolean;
  signupMaintenanceMode?: boolean;
  servicesMaintenanceMode?: boolean;
  maintenanceModeAllServices?: boolean;
  maintenanceServices?: string[];
  maintenanceMessage?: string;
  startDate?: string | null;
  scheduledEndTime?: string | null;
  allowAdminBypass?: boolean;
  supportContact?: string;
  scope?: string;
}

export interface ServiceCatalogItem {
  id: string;
  code: string;
  name: string;
  category: string;
  description: string;
  provider?: string;
  costPrice?: number;
  sellingFee: number;
  serviceCharge?: number;
  commissionRate?: number;
  isActive: boolean;
  displayOrder?: number;
  icon?: string;
  totalVolume?: number;
}

export interface SiteConfig {
  branding: BrandingConfig;
  general: GeneralConfig;
  maintenance: MaintenanceConfig;
  homepage: any;
  navigation: any;
  seo: any;
  social: any;
  servicesCatalog: ServiceCatalogItem[];
  allServices: ServiceCatalogItem[];
  priceMatrix: Record<string, any>;
}

interface SiteConfigContextType {
  config: SiteConfig;
  loading: boolean;
  logoUrl: string;
  primaryColor: string;
  siteName: string;
  maintenanceActive: boolean;
  refreshConfig: () => Promise<void>;
  getServicePrice: (codeOrId: string, defaultPrice?: number) => number;
  getServiceCharge: (codeOrId: string, defaultCharge?: number) => number;
  getServiceItem: (codeOrId: string) => ServiceCatalogItem | undefined;
  isServiceUnderMaintenance: (serviceCode: string) => boolean;
}

const DEFAULT_CONFIG: SiteConfig = {
  branding: {
    siteName: "Smart Link NG",
    tagline: "Enterprise Identity & Corporate Compliance Platform",
    logoUrl: "/logo.png",
    faviconUrl: "/logo.png",
    primaryColor: "#0F2D5C",
    secondaryColor: "#17407E",
    accentColor: "#2563EB",
    headerAnnouncementText: "NIN & BVN Portal Synchronized • Live SLA: 99.98%",
    showHeaderAnnouncement: true,
  },
  general: {
    platformName: "Smart Link NG",
    companyName: "Smart Link Computer Business Solutions Ltd",
    companyAddress: "Federal Capital Territory, Nigeria",
    supportEmail: "support@smartlinkng.com.ng",
    supportPhone: "+234 808 549 0982",
    whatsappNumber: "+234 904 773 8212",
    websiteUrl: "https://smartlinkng.com.ng",
    currency: "NGN (₦)",
  },
  maintenance: {
    maintenanceMode: false,
    maintenanceMessage: "SmartLink is currently undergoing scheduled infrastructure upgrades. Core services will resume shortly.",
    allowAdminBypass: true,
    scheduledEndTime: null,
  },
  homepage: {},
  navigation: {},
  seo: {},
  social: {},
  servicesCatalog: [],
  allServices: [],
  priceMatrix: {},
};

const SETTINGS_CACHE_KEY = "smartlink_public_settings_cache";
const SETTINGS_CACHE_TIME_KEY = "smartlink_public_settings_cache_time";

function getInitialConfig(): SiteConfig {
  if (typeof window !== "undefined") {
    try {
      // Check window initial config first (inlined in HTML)
      const inlined = (window as any).__INITIAL_SITE_CONFIG__;
      if (inlined && typeof inlined === "object") {
        return {
          ...DEFAULT_CONFIG,
          branding: { ...DEFAULT_CONFIG.branding, ...(inlined.branding || {}) },
          general: { ...DEFAULT_CONFIG.general, ...(inlined.general || {}) },
          maintenance: { ...DEFAULT_CONFIG.maintenance, ...(inlined.maintenance || {}) },
          homepage: inlined.homepage || {},
          navigation: inlined.navigation || {},
          seo: inlined.seo || {},
          social: inlined.social || {},
          servicesCatalog: inlined.servicesCatalog || [],
          allServices: inlined.allServices || inlined.servicesCatalog || [],
          priceMatrix: inlined.priceMatrix || {},
        };
      }

      // Check localStorage cache
      const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object") {
          return {
            ...DEFAULT_CONFIG,
            ...parsed,
            branding: { ...DEFAULT_CONFIG.branding, ...(parsed.branding || {}) },
            general: { ...DEFAULT_CONFIG.general, ...(parsed.general || {}) },
            maintenance: { ...DEFAULT_CONFIG.maintenance, ...(parsed.maintenance || {}) },
          };
        }
      }
    } catch {
      // Fallback to DEFAULT_CONFIG
    }
  }
  return DEFAULT_CONFIG;
}

const SiteConfigContext = createContext<SiteConfigContextType>({
  config: DEFAULT_CONFIG,
  loading: false,
  logoUrl: defaultLogoImg,
  primaryColor: "#0F2D5C",
  siteName: "Smart Link Nigeria",
  maintenanceActive: false,
  refreshConfig: async () => {},
  getServicePrice: () => 500,
  getServiceCharge: () => 0,
  getServiceItem: () => undefined,
  isServiceUnderMaintenance: () => false,
});

export const SiteConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<SiteConfig>(getInitialConfig);
  const [loading, setLoading] = useState<boolean>(false);

  const applyThemeVariables = useCallback((branding: BrandingConfig, general: GeneralConfig) => {
    const primary = branding.primaryColor || "#0F2D5C";
    const secondary = branding.secondaryColor || "#17407E";
    const accent = branding.accentColor || "#2563EB";

    // Set CSS custom properties
    document.documentElement.style.setProperty("--color-brand-primary", primary);
    document.documentElement.style.setProperty("--color-brand-secondary", secondary);
    document.documentElement.style.setProperty("--color-brand-accent", accent);

    // Update document title
    const name = general.platformName || branding.siteName || "Smart Link Nigeria";
    const tagline = branding.tagline ? ` | ${branding.tagline}` : "";
    document.title = `${name}${tagline}`;

    // Update favicon and apple-touch-icon (website and app icons)
    const activeFavicon = branding.faviconUrl || "/logo.png";
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = activeFavicon;
    link.type = "image/webp";

    let appleLink: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleLink) {
      appleLink = document.createElement("link");
      appleLink.rel = "apple-touch-icon";
      document.head.appendChild(appleLink);
    }
    appleLink.href = activeFavicon;
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const [res, pricesRes] = await Promise.all([
        fetch("/api/public/settings"),
        fetch("/api/prices"),
      ]);

      let tursoPriceMap: Record<string, number> = {};
      let tursoServices: any[] = [];
      if (pricesRes.ok) {
        try {
          const pricesData = await pricesRes.json();
          if (pricesData.success && pricesData.priceMap) {
            tursoPriceMap = pricesData.priceMap;
            tursoServices = pricesData.services || [];
          }
        } catch (e) {
          // ignore json parse error
        }
      }

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const rawSettings = data.settings || {};
          const safeLogoUrl = rawSettings.logoUrl || rawSettings.lightLogoUrl || defaultLogoImg;
          const brandingData = {
            ...DEFAULT_CONFIG.branding,
            ...(data.branding || {}),
            ...(rawSettings.appName ? { siteName: rawSettings.appName } : {}),
            ...(rawSettings.tagline ? { tagline: rawSettings.tagline } : {}),
            logoUrl: safeLogoUrl,
            ...(rawSettings.primaryColor ? { primaryColor: rawSettings.primaryColor } : {}),
            ...(rawSettings.secondaryColor ? { secondaryColor: rawSettings.secondaryColor } : {}),
            ...(rawSettings.accentColor ? { accentColor: rawSettings.accentColor } : {}),
          };
          const generalData = {
            ...DEFAULT_CONFIG.general,
            ...(data.general || {}),
            ...(rawSettings.appName ? { platformName: rawSettings.appName } : {}),
            ...(rawSettings.contactEmail ? { supportEmail: rawSettings.contactEmail } : {}),
            ...(rawSettings.contactPhone ? { supportPhone: rawSettings.contactPhone } : {}),
            ...(rawSettings.whatsappNumber ? { whatsappNumber: rawSettings.whatsappNumber } : {}),
            ...(rawSettings.currency ? { currency: rawSettings.currency } : {}),
          };

          const rawPriceMatrix = data.priceMatrix || {};
          const mergedPriceMatrix = {
            ...rawPriceMatrix,
            priceMap: {
              ...(rawPriceMatrix.priceMap || {}),
              ...tursoPriceMap,
            },
            tursoServices: tursoServices.length > 0 ? tursoServices : (rawPriceMatrix.tursoServices || []),
          };

          // Overlay Turso prices onto servicesCatalog
          const rawCatalog = data.servicesCatalog || [];
          const updatedCatalog = rawCatalog.map((s: any) => {
            const tursoPrice = tursoPriceMap[s.id] ?? tursoPriceMap[s.code] ?? tursoPriceMap[s.code?.toUpperCase()];
            if (typeof tursoPrice === "number") {
              return { ...s, sellingFee: tursoPrice };
            }
            return s;
          });

          const merged: SiteConfig = {
            branding: brandingData,
            general: generalData,
            maintenance: {
              ...DEFAULT_CONFIG.maintenance,
              ...(data.maintenance || {}),
              ...(rawSettings.maintenanceMode !== undefined ? { maintenanceMode: rawSettings.maintenanceMode } : {}),
              ...(rawSettings.maintenanceMessage ? { maintenanceMessage: rawSettings.maintenanceMessage } : {}),
            },
            homepage: data.homepage || {},
            navigation: data.navigation || {},
            seo: data.seo || {},
            social: data.social || {},
            servicesCatalog: updatedCatalog,
            allServices: data.allServices ? data.allServices.map((s: any) => {
              const tp = tursoPriceMap[s.id] ?? tursoPriceMap[s.code] ?? tursoPriceMap[s.code?.toUpperCase()];
              return typeof tp === "number" ? { ...s, sellingFee: tp } : s;
            }) : updatedCatalog,
            priceMatrix: mergedPriceMatrix,
          };
          setConfig(merged);
          applyThemeVariables(merged.branding, merged.general);

          try {
            localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(merged));
            localStorage.setItem(SETTINGS_CACHE_TIME_KEY, String(Date.now()));
          } catch {
            // Ignore quota errors
          }
        }
      }
    } catch (err) {
      console.warn("[SiteConfig] Could not load public settings:", err);
    } finally {
      setLoading(false);
    }
  }, [applyThemeVariables]);

  useEffect(() => {
    // Non-blocking deferred synchronization to keep initial navigation critical path completely unchained
    let syncScheduled = false;
    const scheduleDeferredSync = () => {
      if (syncScheduled) return;
      syncScheduled = true;

      // Check cache age: if updated within last 5 minutes, defer even further
      const lastSync = Number(localStorage.getItem(SETTINGS_CACHE_TIME_KEY) || "0");
      const isFresh = Date.now() - lastSync < 300000;

      if (isFresh) {
        return;
      }

      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as any).requestIdleCallback(() => fetchConfig(), { timeout: 6000 });
      } else {
        setTimeout(() => fetchConfig(), 4000);
      }
    };

    // Trigger on user interaction or delayed after window load
    const handleInteraction = () => {
      scheduleDeferredSync();
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("pointerdown", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
    };

    window.addEventListener("scroll", handleInteraction, { passive: true });
    window.addEventListener("pointerdown", handleInteraction, { passive: true });
    window.addEventListener("keydown", handleInteraction, { passive: true });

    const loadTimer = setTimeout(scheduleDeferredSync, 5000);

    const handleConfigUpdated = () => {
      fetchConfig();
    };

    window.addEventListener("site_config_updated", handleConfigUpdated);
    window.addEventListener("services_updated", handleConfigUpdated);
    window.addEventListener("prices_updated", handleConfigUpdated);
    window.addEventListener("theme_changed", handleConfigUpdated);
    window.addEventListener("maintenance_mode_triggered", handleConfigUpdated);

    // Broadcast channel / cross-tab storage sync
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "site_config_sync" || e.key === "maintenance_mode" || e.key === SETTINGS_CACHE_KEY || e.key === "prices_updated") {
        fetchConfig();
      }
    };
    window.addEventListener("storage", handleStorage);

    // Polling interval if maintenance mode is active
    let interval: any = null;
    if (config.maintenance?.maintenanceMode) {
      interval = setInterval(fetchConfig, 8000);
    }

    // Re-check on tab focus / visibility
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchConfig();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(loadTimer);
      if (interval) clearInterval(interval);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("pointerdown", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
      window.removeEventListener("site_config_updated", handleConfigUpdated);
      window.removeEventListener("services_updated", handleConfigUpdated);
      window.removeEventListener("prices_updated", handleConfigUpdated);
      window.removeEventListener("theme_changed", handleConfigUpdated);
      window.removeEventListener("maintenance_mode_triggered", handleConfigUpdated);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchConfig, config.maintenance?.maintenanceMode]);

  const logoUrl = config.branding?.logoUrl?.trim()
    ? config.branding.logoUrl
    : defaultLogoImg;

  const primaryColor = config.branding?.primaryColor || "#0F2D5C";
  const rawSiteName = config.branding?.siteName || config.general?.platformName || "Smart Link NG";
  const siteName =
    rawSiteName.toLowerCase().includes("nigeria") || rawSiteName.toLowerCase().includes("enterprise")
      ? "Smart Link NG"
      : rawSiteName;
  const maintenanceActive = Boolean(config.maintenance?.maintenanceMode);

  const getServiceItem = useCallback(
    (codeOrId: string): ServiceCatalogItem | undefined => {
      if (!codeOrId) return undefined;
      const needle = codeOrId.toUpperCase();
      return (
        config.allServices.find(
          (s) =>
            s.code?.toUpperCase() === needle ||
            s.id?.toUpperCase() === needle ||
            s.name?.toUpperCase() === needle
        ) ||
        config.servicesCatalog.find(
          (s) =>
            s.code?.toUpperCase() === needle ||
            s.id?.toUpperCase() === needle ||
            s.name?.toUpperCase() === needle
        )
      );
    },
    [config.allServices, config.servicesCatalog]
  );

  const getServicePrice = useCallback(
    (codeOrId: string, defaultPrice: number = 500): number => {
      if (!codeOrId) return defaultPrice;
      // 1. Direct Turso priceMap check (highest priority: always saved & fetched from Turso)
      const pMap = config.priceMatrix?.priceMap;
      if (pMap) {
        if (typeof pMap[codeOrId] === "number") return pMap[codeOrId];
        if (typeof pMap[codeOrId.toUpperCase()] === "number") return pMap[codeOrId.toUpperCase()];
        if (typeof pMap[codeOrId.toLowerCase()] === "number") return pMap[codeOrId.toLowerCase()];
      }

      // 2. Direct Turso services check
      if (Array.isArray(config.priceMatrix?.tursoServices)) {
        const needle = codeOrId.toLowerCase();
        const found = config.priceMatrix.tursoServices.find(
          (s: any) =>
            String(s.service_id).toLowerCase() === needle ||
            String(s.service_code).toLowerCase() === needle ||
            String(s.name).toLowerCase() === needle
        );
        if (found && typeof found.price === "number") {
          return found.price;
        }
      }

      // 3. Fallback to catalog item sellingFee
      const item = getServiceItem(codeOrId);
      if (item && typeof item.sellingFee === "number" && !isNaN(item.sellingFee)) {
        return item.sellingFee;
      }
      return defaultPrice;
    },
    [config.priceMatrix, getServiceItem]
  );

  const getServiceCharge = useCallback(
    (codeOrId: string, defaultCharge: number = 0): number => {
      const item = getServiceItem(codeOrId);
      if (item && typeof item.serviceCharge === "number" && !isNaN(item.serviceCharge)) {
        return item.serviceCharge;
      }
      return defaultCharge;
    },
    [getServiceItem]
  );

  const isServiceUnderMaintenance = useCallback(
    (serviceCode: string): boolean => {
      if (!serviceCode) return false;
      const m = config.maintenance || {};
      if (m.maintenanceMode) return true;
      if (!m.servicesMaintenanceMode) return false;
      if (m.maintenanceModeAllServices) return true;
      const list = m.maintenanceServices || [];
      const codeUpper = String(serviceCode).toUpperCase().trim();
      return list.some((item) => {
        if (!item) return false;
        const itemUpper = String(item).toUpperCase().trim();
        return itemUpper === codeUpper || codeUpper.includes(itemUpper) || itemUpper.includes(codeUpper);
      });
    },
    [config.maintenance]
  );

  return (
    <SiteConfigContext.Provider
      value={{
        config,
        loading,
        logoUrl,
        primaryColor,
        siteName,
        maintenanceActive,
        refreshConfig: fetchConfig,
        getServicePrice,
        getServiceCharge,
        getServiceItem,
        isServiceUnderMaintenance,
      }}
    >
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => useContext(SiteConfigContext);
